import React, { useCallback, useRef, useState } from "react";

import { useRouteBuilder } from "../../context/RouteBuilderContext";
import { MAX_FOCUSED_ROUTES } from "../../lib/routeFocus";
import { resolveRouteColorPalette } from "../../../../../../components/Maps/ActiveRoutes/routeColor";
import { useDismissiblePopover } from "../../../../../../components/Inputs/useDismissiblePopover";

export default function RouteFocusBar() {
  const {
    session,
    needsRouteFocusMode,
    focusedRouteIds,
    visibleRoutes,
    setFocusedRoutes,
    clearRouteFocus,
  } = useRouteBuilder();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  const dismissPicker = useCallback(() => setPickerOpen(false), []);
  useDismissiblePopover(pickerOpen, rootRef, dismissPicker);

  if (!session || !needsRouteFocusMode) {
    return null;
  }

  const total = session.routes.length;
  const showing = visibleRoutes.length;
  const isFocused = Boolean(focusedRouteIds?.length);

  const openPicker = () => {
    setDraftIds(
      focusedRouteIds?.length
        ? [...focusedRouteIds]
        : session.routes
            .slice(0, Math.min(5, MAX_FOCUSED_ROUTES))
            .map((r) => r.id),
    );
    setPickerOpen(true);
  };

  const toggleDraft = (routeId: string) => {
    setDraftIds((prev) => {
      if (prev.includes(routeId)) {
        return prev.filter((id) => id !== routeId);
      }
      if (prev.length >= MAX_FOCUSED_ROUTES) return prev;
      return [...prev, routeId];
    });
  };

  return (
    <div
      ref={rootRef}
      className="relative flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2"
      data-testid="route-focus-bar"
    >
      <span className="text-xs text-slate-600">
        Showing <strong>{showing}</strong> of <strong>{total}</strong> routes
        {isFocused ? " (focused)" : ""}
      </span>
      <button
        type="button"
        data-testid="route-focus-edit-button"
        className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        onClick={() => (pickerOpen ? setPickerOpen(false) : openPicker())}
      >
        {isFocused ? "Edit focus" : "Focus…"}
      </button>
      {isFocused ? (
        <button
          type="button"
          data-testid="route-focus-clear-button"
          className="rounded-lg px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50"
          onClick={clearRouteFocus}
        >
          Show all
        </button>
      ) : null}
      <span className="text-[11px] text-slate-400">
        Focus up to {MAX_FOCUSED_ROUTES} routes for easier drag-and-drop
      </span>

      {pickerOpen ? (
        <div
          className="absolute left-3 top-full z-50 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
          data-testid="route-focus-picker"
        >
          <p className="text-xs font-semibold text-slate-800">
            Focus routes ({draftIds.length}/{MAX_FOCUSED_ROUTES})
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">
            Choose a working set to show on the board.
          </p>
          <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
            {session.routes.map((route) => {
              const checked = draftIds.includes(route.id);
              const atCap = draftIds.length >= MAX_FOCUSED_ROUTES && !checked;
              const palette = resolveRouteColorPalette(route.color);
              return (
                <li key={route.id}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
                      atCap ? "opacity-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`route-focus-option-${route.id}`}
                      checked={checked}
                      disabled={atCap}
                      onChange={() => toggleDraft(route.id)}
                    />
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${palette.bg}`}
                    />
                    <span className="truncate text-slate-800">
                      {route.name}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-lg px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              onClick={() => setPickerOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="route-focus-apply-button"
              disabled={draftIds.length === 0}
              className="rounded-lg bg-snow-primary px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
              onClick={() => {
                setFocusedRoutes(draftIds);
                setPickerOpen(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
